/**
 * 库存管理 - 常量定义
 * 
 * @module types/inventory/constants
 * @description 定义库存批次相关的常量映射、选项列表
 * 
 * 📋 对应后端文件
 * - `backend/app/Modules/Inventory/Models/InventoryBatch.php` - getQualityStatuses() 等静态方法
 * 
 * ⚠️ 注意事项
 * - 常量对象的 key 必须与枚举值完全一致
 * - 所有中文标签必须与后端保持一致
 * - 用于前端下拉选择、状态显示等场景
 * 
 * 🔄 更新时机
 * - Model 静态方法返回值变更时
 * - 新增状态或类型时
 * - 中文文案调整时
 * 
 * ✅ 验证方式
 * - 对照 Model 的静态方法（getQualityStatuses 等）
 * - 确保所有枚举值都有对应的中文标签
 * 
 * @last-updated 2025-10-18
 */

import {
  InventoryBatchQualityStatus,
  InventoryBatchStatus,
  InventoryLockSource,
} from './enums'

/**
 * 质量状态中文标签映射
 */
export const INVENTORY_BATCH_QUALITY_STATUS_LABELS: Record<InventoryBatchQualityStatus, string> = {
  [InventoryBatchQualityStatus.QUALIFIED]: '合格',
  [InventoryBatchQualityStatus.PENDING]: '待检',
  [InventoryBatchQualityStatus.REJECTED]: '不合格',
  [InventoryBatchQualityStatus.QUARANTINE]: '隔离',
}

/**
 * 批次状态中文标签映射
 */
export const INVENTORY_BATCH_STATUS_LABELS: Record<InventoryBatchStatus, string> = {
  [InventoryBatchStatus.ACTIVE]: '正常',
  [InventoryBatchStatus.FROZEN]: '冻结',
}

/**
 * 锁定来源节点中文标签映射（与后端 InventoryLock::sourceTypeLabels() 一致）
 */
export const INVENTORY_LOCK_SOURCE_LABELS: Record<InventoryLockSource, string> = {
  [InventoryLockSource.FREEZE]: '手动冻结',
  [InventoryLockSource.NCR]: '质量隔离（NCR）',
  [InventoryLockSource.PURCHASE_RETURN]: '采购退货',
  [InventoryLockSource.INIT]: '建账待审核',
  [InventoryLockSource.MANUAL]: '手动锁定',
}

/**
 * 锁定来源节点处理指引（与后端 InventoryLock::sourceTypeGuides() 一致，
 * 告知员工针对每个来源应到哪个节点处理）
 */
export const INVENTORY_LOCK_SOURCE_GUIDES: Record<InventoryLockSource, string> = {
  [InventoryLockSource.FREEZE]: '请联系仓库管理员在库存管理中解冻该批次',
  [InventoryLockSource.NCR]: '请在质量管理-不合格品单（NCR）中完成处置后解锁',
  [InventoryLockSource.PURCHASE_RETURN]: '请等待采购退货单审核/出库完成后自动解锁',
  [InventoryLockSource.INIT]: '请在库存建账中对初始化单据执行审核，审核通过自动解锁',
  [InventoryLockSource.MANUAL]: '请联系锁定人确认原因解决后手动解锁',
}

/**
 * ⚠️ MATERIAL_TYPE_LABELS 已移至 types/material/constants.ts
 * 
 * 请使用：
 * ```typescript
 * import { MATERIAL_TYPE_LABELS } from '../material'
 * ```
 * 
 * @deprecated 请从 @bayes-mes/shared/types/material 导入
 */
// export const MATERIAL_TYPE_LABELS = { ... } - 已移至 types/material/constants.ts

/**
 * 质量状态选项列表（用于下拉选择）
 */
export const INVENTORY_BATCH_QUALITY_STATUS_OPTIONS = [
  { value: InventoryBatchQualityStatus.QUALIFIED, label: '合格' },
  { value: InventoryBatchQualityStatus.PENDING, label: '待检' },
  { value: InventoryBatchQualityStatus.REJECTED, label: '不合格' },
  { value: InventoryBatchQualityStatus.QUARANTINE, label: '隔离' },
]

/**
 * 批次状态选项列表（用于下拉选择）
 */
export const INVENTORY_BATCH_STATUS_OPTIONS = [
  { value: InventoryBatchStatus.ACTIVE, label: '正常' },
  { value: InventoryBatchStatus.FROZEN, label: '冻结' },
]

/**
 * ⚠️ MATERIAL_TYPE_OPTIONS 已移至 types/material/constants.ts
 * 
 * 请使用：
 * ```typescript
 * import { MATERIAL_TYPE_OPTIONS } from '../material'
 * ```
 * 
 * @deprecated 请从 @bayes-mes/shared/types/material 导入
 */
// export const MATERIAL_TYPE_OPTIONS = [ ... ] - 已移至 types/material/constants.ts

/**
 * 排序字段选项
 */
export const INVENTORY_BATCH_SORT_FIELDS = [
  { value: 'inbound_date', label: '入库日期' },
  { value: 'expiry_date', label: '过期日期' },
  { value: 'quantity', label: '库存数量' },
  { value: 'available_quantity', label: '可用数量' },
  { value: 'batch_no', label: '批次号' },
  { value: 'material_code', label: '物料编码' },
  { value: 'created_at', label: '创建时间' },
]

/**
 * 默认查询参数
 */
export const DEFAULT_INVENTORY_BATCH_QUERY_PARAMS = {
  per_page: 20,
  page: 1,
  sort_field: 'inbound_date',
  sort_order: 'asc' as const,
  only_active: true,
}

/**
 * 默认即将过期天数
 */
export const DEFAULT_EXPIRING_SOON_DAYS = 30

/**
 * 库存业务类型
 */
export const INVENTORY_BUSINESS_TYPES = {
  PURCHASE_IN: 'PURCHASE_IN', // 采购入库
  PRODUCTION_IN: 'PRODUCTION_IN', // 生产入库
  RETURN_IN: 'RETURN_IN', // 退货入库
  TRANSFER_IN: 'TRANSFER_IN', // 调拨入库
  OTHER_IN: 'OTHER_IN', // 其他入库
  MATERIAL_OUT: 'MATERIAL_OUT', // 领料出库
  SALE_OUT: 'SALE_OUT', // 销售出库
  TRANSFER_OUT: 'TRANSFER_OUT', // 调拨出库
  SCRAP_OUT: 'SCRAP_OUT', // 报废出库
  OTHER_OUT: 'OTHER_OUT', // 其他出库
  INIT: 'INIT', // 初始化盘点
}

/**
 * 库存业务类型中文标签
 */
export const INVENTORY_BUSINESS_TYPE_LABELS: Record<string, string> = {
  [INVENTORY_BUSINESS_TYPES.PURCHASE_IN]: '采购入库',
  [INVENTORY_BUSINESS_TYPES.PRODUCTION_IN]: '生产入库',
  [INVENTORY_BUSINESS_TYPES.RETURN_IN]: '退货入库',
  [INVENTORY_BUSINESS_TYPES.TRANSFER_IN]: '调拨入库',
  [INVENTORY_BUSINESS_TYPES.OTHER_IN]: '其他入库',
  [INVENTORY_BUSINESS_TYPES.MATERIAL_OUT]: '领料出库',
  [INVENTORY_BUSINESS_TYPES.SALE_OUT]: '销售出库',
  [INVENTORY_BUSINESS_TYPES.TRANSFER_OUT]: '调拨出库',
  [INVENTORY_BUSINESS_TYPES.SCRAP_OUT]: '报废出库',
  [INVENTORY_BUSINESS_TYPES.OTHER_OUT]: '其他出库',
  [INVENTORY_BUSINESS_TYPES.INIT]: '初始化盘点',
}

